/* eslint-disable */

/**
 * MODIF 2026-09-07 (high-variant) — Jardin-Confort
 *
 * Réécriture du variant picker Enterprise 1.6.2 pour les produits de plus de 250 variantes.
 * Shopify plafonne `product.variants` et `product | json` à 250 variantes dans Liquid : l'ancien
 * picker cherchait la variante sélectionnée dans ce JSON et affichait « Indisponible » au-delà.
 *
 * Nouveau fonctionnement :
 *  - Le JSON embarqué ne contient plus que la variante courante (+ prix formatés).
 *  - L'existence / disponibilité de chaque valeur d'option est rendue par Liquid
 *    (data-exists / data-available sur chaque .js-option, via product_option_value.variant).
 *  - À chaque changement d'option, on recharge la section produit via la Section Rendering API
 *    (`{product.url}?option_values=<ids>&section_id=<section>`), puis on remplace dans la page :
 *    prix, SKU, code-barres, poids, stock (product-inventory), backorder, étiquettes, retrait en
 *    magasin, états des options, input[name="id"] et bouton d'achat.
 *  - L'événement `on:variant:change` est conservé (detail.variant = objet variante JSON,
 *    detail.product = { id, url }) pour media-gallery.js, product-inventory.js, variant-label.js…
 *
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.data.variant || null;
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;
      this.abortController = null;
      this.requestId = 0;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      const ids = this.getSelectedOptionValueIds();
      const allSelected = this.selectedOptions.find((o) => o === null) === undefined;

      if (!allSelected || ids.length !== this.selectedOptions.length) {
        // Sélection incomplète (mode « Veuillez sélectionner ») : pas de variante.
        this.variant = null;
        this.preSelection = true;
        this.updateAddToCartButton();
        this.updatePrice();
        this.updateAvailability();
        return;
      }

      this.preSelection = false;
      this.fetchSection(ids, evt);
    }

    /**
     * Recharge la section produit pour la sélection d'options donnée (Section Rendering API).
     * @param {Array} ids - IDs des valeurs d'option sélectionnées.
     * @param {object} evt - Event object.
     */
    async fetchSection(ids, evt) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const requestId = ++this.requestId;
      const sectionId = this.dataset.section;
      const url = `${this.dataset.url.split('?')[0]}?option_values=${ids.join(',')}&section_id=${sectionId}`;

      this.classList.add('is-loading');
      if (this.addBtn) this.addBtn.classList.add('is-loading');

      let html;
      try {
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
        html = await response.text();
      } catch (e) {
        if (e.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.error('[variant-picker] impossible de recharger la section produit', e);
        this.classList.remove('is-loading');
        if (this.addBtn) this.addBtn.classList.remove('is-loading');
        return;
      }

      if (requestId !== this.requestId) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newPicker = doc.querySelector('variant-picker');
      if (!newPicker) {
        this.classList.remove('is-loading');
        if (this.addBtn) this.addBtn.classList.remove('is-loading');
        return;
      }

      const dataEl = newPicker.querySelector('[type="application/json"]');
      let newData = null;
      try {
        newData = JSON.parse(dataEl.textContent);
      } catch (e) {
        newData = null;
      }

      if (newData) {
        this.data = newData;
        // `selected` = la combinaison correspond à une vraie variante (product.selected_variant).
        this.variant = newData.selected && newData.variant ? newData.variant : null;
      } else {
        this.variant = null;
      }

      this.syncOptionStates(newPicker);
      this.swapSectionBlocks(doc);

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      if (evt && evt.target) VariantPicker.updateLabelText(evt);

      this.classList.remove('is-loading');
      if (this.addBtn) this.addBtn.classList.remove('is-loading');

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          inventory: this.inventoryDetail || null,
          product: { id: this.data.productId, url: this.data.url }
        }
      }));
    }

    /**
     * Copie les états (existence / disponibilité / image / statut stock) des options depuis le picker
     * fraîchement rendu vers le picker en place, sans toucher à la sélection ni au custom-select.
     * @param {Element} newPicker - <variant-picker> issu du HTML rechargé.
     */
    syncOptionStates(newPicker) {
      const fresh = {};
      newPicker.querySelectorAll('.js-option[data-option-value-id]').forEach((el) => {
        fresh[el.dataset.optionValueId] = el;
      });

      this.querySelectorAll('.js-option[data-option-value-id]').forEach((el) => {
        const src = fresh[el.dataset.optionValueId];
        if (!src) return;
        el.dataset.exists = src.dataset.exists;
        el.dataset.available = src.dataset.available;
        if (src.dataset.variantId) {
          el.dataset.variantId = src.dataset.variantId;
        } else {
          delete el.dataset.variantId;
        }
        if (src.dataset.stockStatus) {
          el.dataset.stockStatus = src.dataset.stockStatus;
        } else {
          delete el.dataset.stockStatus;
        }

        // Pastilles « variant-images » : mettre à jour l'image si la variante a changé
        if (el.tagName === 'INPUT') {
          const label = el.nextElementSibling;
          const srcLabel = src.nextElementSibling;
          if (label && srcLabel && label.classList.contains('opt-label--image') && srcLabel.classList.contains('opt-label--image')) {
            const media = label.querySelector('.opt-label__media');
            const srcMedia = srcLabel.querySelector('.opt-label__media');
            if (media && srcMedia && media.innerHTML.trim() !== srcMedia.innerHTML.trim() && srcMedia.innerHTML.trim() !== '') {
              media.innerHTML = srcMedia.innerHTML;
              label.classList.add('swatch--variant-image');
              label.removeAttribute('data-swatch');
            }
          }
        }
      });

      this.updateAvailability();
    }

    /**
     * Remplace dans la page les blocs qui dépendent de la variante par ceux de la section rechargée.
     * @param {Document} doc - Document HTML de la section rechargée.
     */
    swapSectionBlocks(doc) {
      const newSection = doc.querySelector('.js-product');
      if (!newSection) return;

      const swapHtml = (selector) => {
        const cur = this.section.querySelector(selector);
        const next = newSection.querySelector(selector);
        if (cur && next) cur.innerHTML = next.innerHTML;
      };

      const swapNode = (selector) => {
        const cur = this.section.querySelector(selector);
        const next = newSection.querySelector(selector);
        if (cur && next) {
          cur.replaceWith(document.importNode(next, true));
        } else if (cur && !next) {
          cur.hidden = true;
        }
      };

      // Prix (rendu Liquid complet : prix, prix barré, prix unitaire, badge)
      swapHtml('.product-info__price');
      // Stock / délai : on garde l'élément <product-inventory> en place (son init est paresseuse,
      // IntersectionObserver) et on lui transmet les données de la nouvelle variante via l'événement.
      this.inventoryDetail = null;
      const curInv = this.section.querySelector('product-inventory');
      const nextInv = newSection.querySelector('product-inventory');
      if (curInv && nextInv) {
        ['inventoryQuantity', 'inventoryPolicy', 'variantAvailable', 'delaiSemaines'].forEach((k) => {
          curInv.dataset[k] = nextInv.dataset[k] || '';
        });
        this.inventoryDetail = {
          inventory_quantity: parseInt(nextInv.dataset.inventoryQuantity, 10) || 0,
          available: nextInv.dataset.variantAvailable === 'true',
          inventory_policy: nextInv.dataset.inventoryPolicy,
          delai_semaines: nextInv.dataset.delaiSemaines || ''
        };
      } else if (curInv && !nextInv) {
        curInv.hidden = true;
      }
      // Sur commande (texte backorder)
      swapHtml('.product-backorder');
      // Étiquettes de variante
      swapNode('variant-label');
      // Versements (input id mis à jour par updateVariantInput)
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {
        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors, from the data-exists / data-available
     * attributes rendered by Liquid (product_option_value.variant, relative to the current selection).
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;

      this.querySelectorAll('.js-option[data-option-value-id]').forEach((optionEl) => {
        const exists = optionEl.dataset.exists === 'true';
        const available = optionEl.dataset.available === 'true';
        VariantPicker.updateOptionAvailability(optionEl, exists, available);
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const formatted = this.data.formatted[this.variant.id] || {};
        const { inventory } = formatted;

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          if (backorderProdEl && prodTitleEl) {
            backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          }
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (!selector || selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      const valueEl = evt.target.nextElementSibling && evt.target.nextElementSibling.querySelector('.js-value');
      if (valueEl) colorText.textContent = valueEl.textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     * Le HTML du prix est déjà remplacé par swapSectionBlocks() ; on ne gère ici que les états
     * (variante inexistante / présélection / prix « à partir de »).
     */
    updatePrice() {
      this.price = this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const formatted = this.data.formatted[this.variant.id];
        if (priceCurrentEl && formatted && formatted.price) {
          priceCurrentEl.innerHTML = formatted.price;
        }
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      const priceDefault = this.price.querySelector('.price__default');
      const priceNoVariant = this.price.querySelector('.price__no-variant');
      if (priceDefault) priceDefault.hidden = !this.variant && !this.preSelection;
      if (priceNoVariant) priceNoVariant.hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const formatted = this.variant ? (this.data.formatted[this.variant.id] || {}) : {};
      const weightAvailable = this.variant && this.variant.weight > 0 && formatted.weight;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? formatted.weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the selected option value IDs (product_option_value.id) from each selector.
     * @returns {Array}
     */
    getSelectedOptionValueIds() {
      const ids = [];

      this.optionSelectors.forEach((selector) => {
        let el;
        if (selector.dataset.selectorType === 'dropdown') {
          el = selector.querySelector('.custom-select__option[aria-selected="true"][data-option-value-id]');
        } else {
          el = selector.querySelector('input:checked[data-option-value-id]');
        }
        if (el && el.dataset.optionValueId) ids.push(el.dataset.optionValueId);
      });

      return ids;
    }

    /**
     * Gets the product data (current variant only).
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      try {
        return JSON.parse(dataEl.textContent);
      } catch (e) {
        return { variant: null, selected: false, formatted: {} };
      }
    }

    /**
     * Get selected variant data (variante courante, chargée par Liquid).
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.variant;
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
